import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Constants } from '@app/constants';
import { AppErrorService } from '@app/core/services/app-error.service';
import { AppErrorStateService } from '@app/core/services/app-error-state.service';
import { LoggingService } from '@app/core/services/logging.service';
import { SpinnerStateService } from '@app/core/services/spinner-state.service';
import { UnityDirectStateService } from '@app/core/services/unity-direct-state.service';
import { AppNames } from '@app/models/AppNames';
import { LoanStatus } from '@app/models/Loan-Status';
import { Pages } from '@app/models/Pages';
import { Steps } from '@app/models/unity-direct.model';
import { loanStatusText } from '@app/shared/constants/loanStatusText';
import { Subject } from 'rxjs';
import { environment } from 'src/environments/environment';

import { AppErrorDetails } from './error.model';
import { NotificationService } from '@app/core/services/notification.service';

@Component({
  selector: 'app-error',
  templateUrl: './error.component.html',
  styleUrls: ['./error.component.scss']
})
export class ErrorComponent implements OnInit, AfterViewInit, OnDestroy {
  error: AppErrorDetails | null = null;
  helpEmail: string;
  buttonText: string;
  currentLoanStatus: string;
  id: string;
  loanStatus: LoanStatus;
  isShowErrorPanel: boolean = false;
  canUserAccessNewQuote: boolean;
  canUserAccessBilling: boolean;
  isAppDirect: boolean;
  onDestroy$: Subject<boolean> = new Subject<boolean>();

  readonly isProductionEnv = environment.production;

  constructor(
    private readonly router: Router,
    private readonly ls: LoggingService,
    private readonly appErrorService: AppErrorService,
    private readonly udStateService: UnityDirectStateService,
    private readonly spinnerStateService: SpinnerStateService,
    private readonly appErrorStateService: AppErrorStateService,
    private readonly notificationService: NotificationService
  ) {
    this.id = this.router.getCurrentNavigation()?.extras.state?.id;
    this.loanStatus = this.router.getCurrentNavigation()?.extras.state?.status;
    this.isShowErrorPanel = !this.isProductionEnv;
    this.helpEmail = Constants.appConfiguration.helpEmail;
  }

  ngOnInit(): void {
    const appName = sessionStorage.getItem('appName');
    this.isAppDirect = appName === AppNames.UNITY_DIRECT;
    this.canUserAccessNewQuote = this.udStateService.canUserAccessNewQuote;
    this.canUserAccessBilling = this.udStateService.canUserAccessBilling;
    this.changeErrorButtonTest();
    this.error = this.appErrorStateService.getError();
    if (!this.error) {
      this.router.navigate(['/']);
    }
  }

  ngAfterViewInit(): void {
    this.ls.sendInfoLevelMessage('Error page loaded successfully');
  }

  changeErrorButtonTest(): void {
    if (
      ((this.isAppDirect && this.canUserAccessNewQuote) || !this.isAppDirect) &&
      (this.loanStatus === loanStatusText[LoanStatus.New] ||
        this.id === undefined)
    ) {
      this.buttonText = 'New Quote';
    } else if (
      (this.isAppDirect && this.canUserAccessNewQuote) ||
      !this.isAppDirect
    ) {
      this.buttonText = 'Review';
    } else if (this.isAppDirect && this.canUserAccessBilling) {
      this.buttonText = 'Billing';
    }
  }
  /**
   * If LoanStatus is New Quote or ID is undefined(when route changed dynamically) redirect to Main Page or else redirect to Review
   */
  redirect(): void {
    const canAccessDirectApp =
      this.isAppDirect &&
      (this.canUserAccessNewQuote || this.canUserAccessBilling);

    if (!canAccessDirectApp) {
      this.router.navigate([Pages.UnityExpress]);
      return;
    }

    if (!this.id) {
      this.redirectWithoutLoan();
      return;
    }

    if (this.loanStatus !== loanStatusText[LoanStatus.New]) {
      this.router.navigate([Pages.Review], {
        state: { id: this.id, status: this.loanStatus }
      });
      return;
    }

    this.redirectToNewQuote();
  }

  private redirectWithoutLoan(): void {
    if (this.canUserAccessNewQuote) {
      this.router.navigate(['/']);
      return;
    }

    if (this.canUserAccessBilling) {
      this.router.navigate([Pages.Billing]);
    }
  }

  private redirectToNewQuote(): void {
    this.spinnerStateService.show();

    this.router.navigate(['/']);
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';

    this.udStateService.resetState();
    this.udStateService.isProductsRatesAvailableSubject.next(false);
    this.udStateService.currentStep.next(Steps['VehicleInfo']);
    this.udStateService.isResetUDFormSubject.next(true);
    this.udStateService.loanStatusSubject.next(LoanStatus.New);

    setTimeout(() => {
      // This is only a cosmetic delay to let the user know that something has changed.
      this.spinnerStateService.hide();
    }, 1000);
  }

  async copyErrorToClipboard(): Promise<void> {
    if (!this.error) return;

    const details = JSON.stringify(this.error, null, 2);

    try {
      await navigator.clipboard.writeText(details);
      this.notificationService.showError('Copied error.');
    } catch (error) {
      this.notificationService.showError('Failed to copy error.');
      console.error('Unable to copy error details:', error);
    }
  }

  contactSWBC(): void {
    if (!this.error) {
      globalThis.location.href = `mailto:${this.helpEmail}`;
      return;
    }

    const subject = 'Error Report';
    const body = [
      'An application error occurred.',
      '',
      `App: ${this.error.app}`,
      `Time: ${new Date(this.error.timestamp).toLocaleString()}`,
      `Environment: ${this.error.environment}`,
      `Page: ${this.error.route}`,
      `Error: ${this.error.message}`,
      this.error.status === undefined
        ? ''
        : `HTTP Status: ${this.error.status}`,
      this.error.method ? `HTTP Method: ${this.error.method}` : '',
      this.error.url ? `API: ${this.error.url}` : ''
    ]
      .filter(line => line !== '')
      .join('\n');

    const mailTo =
      `mailto:${this.helpEmail}` +
      `?subject=${encodeURIComponent(subject)}` +
      `&body=${encodeURIComponent(body)}`;

    globalThis.location.href = mailTo;
  }

  ngOnDestroy(): void {
    this.appErrorService.resetFatalState();
    this.appErrorStateService.clearError();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
